import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { interval, of } from 'rxjs';
import { State, Store } from '@ngrx/store';
import * as FeatureFlagActions from './feature-flag.actions';
import { APP_CONFIGURATION, AppConfiguration } from '../../app/app-configuration';
import { FeatureFlagState } from './feature-flag.reducer';
import { FeatureFlagService } from 'projects/uzurv/src/services/feature-flag/feature-flag.service';
import { featureFlagState } from './feature-flag.selectors';
import { FeatureFlag } from 'projects/uzurv/src/services/feature-flag/feature-flag.interfaces';
import { AMPLITUDE_CONFIGURATION, AmplitudeConfiguration } from '@uzurv/core';

@Injectable()
export class FeatureFlagEffects {
  constructor(
    @Inject(APP_CONFIGURATION) public appConfiguration: AppConfiguration,
    @Inject(AMPLITUDE_CONFIGURATION) protected amplitudeConfiguration: AmplitudeConfiguration,
    private actions$: Actions,
    private featureFlagService: FeatureFlagService,
    private store$: Store<State<FeatureFlagState>>
  ) {}

  initializeFeatureFlags$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FeatureFlagActions.InitializeFeatureFlags),
      withLatestFrom(this.store$.select(featureFlagState)),
      switchMap(() => {
        return this.featureFlagService.initializeFeatureFlags().pipe(
          map(() => {
            return FeatureFlagActions.InitializeFeatureFlagsSuccess();
          }),
          catchError(error => of(FeatureFlagActions.InitializeFeatureFlagsFail({ error })))
        );
      })
    );
  });

  rehydrateFeatureFlags$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FeatureFlagActions.RehydrateFeatureFlags, FeatureFlagActions.InitializeFeatureFlags),
      withLatestFrom(this.store$.select(featureFlagState)),
      map(() => {
        const featureFlags: Record<string, FeatureFlag> = this.featureFlagService.readCurrentFlagsFromCache();
        return FeatureFlagActions.RehydrateFeatureFlagsSuccess({ flags: featureFlags });
      })
    );
  });

  fetchFeatureFlags$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FeatureFlagActions.InitializeFeatureFlagsSuccess),
      withLatestFrom(this.store$.select(featureFlagState)),
      switchMap(() => {
        return this.handleFetchFeatureFlags();
      })
    );
  });

  pollFeatureFlags$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeatureFlagActions.InitializeFeatureFlagsSuccess),
      filter(() => {
        return this.amplitudeConfiguration.enableFeatureFlagPolling;
      }),
      switchMap(() => {
        return interval(this.amplitudeConfiguration.featureFlagPollingSeconds * 1000).pipe(
          switchMap(() => {
            return this.handleFetchFeatureFlags();
          }),
          takeUntil(this.actions$.pipe(ofType(FeatureFlagActions.StopPollingFeatureFlags)))
        );
      })
    )
  );

  trackExposureEvent$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(FeatureFlagActions.TrackExposureEvent),
        withLatestFrom(this.store$.select(featureFlagState)),
        map(([action]) => {
          this.featureFlagService.trackExposure(action.flagCode);
        })
      );
    },
    { dispatch: false }
  );

  private handleFetchFeatureFlags() {
    return this.featureFlagService.fetchFeatureFlags().pipe(
      map(featureFlags => {
        this.featureFlagService.writeCurrentFlagsToCache(featureFlags);
        return FeatureFlagActions.FetchFeatureFlagsSuccess({ flags: featureFlags });
      }),
      catchError(error => of(FeatureFlagActions.InitializeFeatureFlagsFail({ error })))
    );
  }
}
